import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Theme,
  useTheme,
} from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { get } from 'lodash';
import { useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSystemSettings } from '../../../context/SystemSettingsContext';
import { WORDS_ONLY_REGEX } from '../../../utils/regexHelpers';
import { roleNameMap } from '../../../utils/roleNameMap';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export interface CreateUserInput {
  firstName: string;
  lastName: string;
  email: string;
  roles: string[];
}

export default function AddUserDialog({
  isOpen,
  handleClose,
  onSubmit,
}: {
  isOpen: boolean;
  handleClose: () => void;
  onSubmit: SubmitHandler<CreateUserInput>;
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  const { roles: roleOptions } = useSystemSettings();

  const { reset, control, handleSubmit } = useForm<CreateUserInput>({
    mode: 'all',
  });

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const closeDialog = () => {
    handleClose();
  };

  function getStyles(name: string, roles: readonly string[], theme: Theme) {
    return {
      fontWeight:
        roles.indexOf(name) === -1
          ? theme.typography.fontWeightRegular
          : theme.typography.fontWeightMedium,
    };
  }

  return (
    <Box>
      <Dialog open={isOpen} onClose={handleClose}>
        <DialogTitle>{t('USERS.ADD_NEW_USER')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('USERS.INVITE_DESCRIPTION')}</DialogContentText>
          <Controller
            name="firstName"
            control={control}
            defaultValue={''}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                value={value}
                onChange={onChange}
                fullWidth
                error={!!error}
                label={t('USERS.INVITE_NAME')}
                helperText={error ? error.message : null}
                sx={{
                  marginBottom: '20px',
                  marginTop: '20px',
                }}
                inputProps={{ 'data-testid': 'userNameInput' }}
              />
            )}
            rules={{
              required: `${t('USERS.INVITE_NAME')} ${t(
                'USERS.INVITE_REQUIRED'
              )}`,
              pattern: {
                value: WORDS_ONLY_REGEX,
                message: t('USERS.INVITE_NAME_VALIDATION'),
              },
              maxLength: {
                value: 50,
                message: t('VALIDATION.MAX_LENGTH', { length: 50 }),
              },
            }}
          />

          <Controller
            name="lastName"
            control={control}
            defaultValue={''}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                value={value}
                onChange={onChange}
                error={!!error}
                label={t('USERS.INVITE_LASTNAME')}
                helperText={error ? error.message : null}
                sx={{
                  marginBottom: '20px',
                  marginTop: '20px',
                }}
                inputProps={{ 'data-testid': 'userNameInput' }}
              />
            )}
            rules={{
              required: `${t('USERS.INVITE_LASTNAME')} ${t(
                'USERS.INVITE_REQUIRED'
              )}`,
              pattern: {
                value: WORDS_ONLY_REGEX,
                message: t('USERS.INVITE_NAME_VALIDATION'),
              },
              maxLength: {
                value: 50,
                message: t('VALIDATION.MAX_LENGTH', { length: 50 }),
              },
            }}
          />

          <Controller
            name="email"
            control={control}
            defaultValue={''}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                value={value}
                fullWidth
                onChange={onChange}
                error={!!error}
                label={'Email'}
                helperText={error ? error.message : null}
                sx={{
                  marginBottom: '20px',
                  marginTop: '20px',
                }}
                inputProps={{ 'data-testid': 'userNameInput' }}
              />
            )}
            rules={{
              required: `${'Email'} ${t('USERS.INVITE_REQUIRED')}`,
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                message: t('USERS.INVITE_ROLE.EMAIL_VALIDATION'),
              },
              maxLength: {
                value: 50,
                message: t('VALIDATION.MAX_LENGTH', { length: 50 }),
              },
            }}
          />

          <Controller
            name="roles"
            control={control}
            defaultValue={[]}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormControl fullWidth>
                <InputLabel id="demo-simple-select-label">
                  {t('USERS.INVITE_ROLE')}
                </InputLabel>
                <Select
                  labelId="demo-multiple-chip-label"
                  data-testid={'role-select'}
                  id="role-select"
                  // multiple
                  value={value}
                  error={!!error}
                  onChange={(e) => {
                    onChange([e.target.value]);
                  }}
                  sx={{
                    minWidth: '100px',
                  }}
                  inputProps={{ 'data-testid': 'userRoleInput' }}
                  input={
                    <OutlinedInput id="select-multiple-chip" label="Chip" />
                  }
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={t(get(roleNameMap, value))} />
                      ))}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                >
                  {roleOptions?.map((roleOption: string, index: number) => (
                    <MenuItem
                      key={roleOption}
                      value={roleOption}
                      data-testid={`roleOption-${index}`}
                      style={getStyles(roleOption, value, theme)}
                    >
                      {t(get(roleNameMap, roleOption))}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {error
                    ? `${t('USERS.INVITE_ROLE')} ${t('USERS.INVITE_REQUIRED')}`
                    : ''}
                </FormHelperText>
              </FormControl>
            )}
            rules={{
              required: `${t('USERS.INVITE_ROLE')} ${t(
                'USERS.INVITE_REQUIRED'
              )}`,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} data-testid="cancelDialog">
            {t('USERS.INVITE_CANCEL')}
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            // disabled={!email || !roles.length || !firstName || !lastName}
            data-testid="inviteButton"
          >
            {t('USERS.INVITE_SUBMIT')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
